import { addToCart, updateCartItemQuantity } from "./cart";
import { decideFreeGiftAction, findFreeGiftLine, FREE_GIFT_VARIANT_ID, type FreeGiftCustomer } from "./free-gift-promo";

declare global {
  interface Window {
    FREE_GIFT_CUSTOMER?: FreeGiftCustomer;
  }
}

let isSyncing = false;
let needsResync = false;

// This repo's cart.ts (unlike tms-shopify's) doesn't check response.ok on cart/add.js
// or cart/change.js — a failed request (e.g. a bad variant ID, or the gift's redemption
// cap being exhausted) still resolves normally and still fires cranky:cart:update, which
// would otherwise re-trigger the same "add"/"remove" decision forever. Give up after a
// few unproductive attempts at the same action instead of retrying without bound.
const MAX_CONSECUTIVE_ATTEMPTS = 3;
let lastAttemptedAction: "add" | "remove" | null = null;
let consecutiveAttempts = 0;

async function syncFreeGiftLine() {
  if (isSyncing) {
    needsResync = true;
    return;
  }
  isSyncing = true;

  try {
    const cart = window.Alpine.store("cart");
    const action = decideFreeGiftAction({
      subtotalCents: cart.items_subtotal_price,
      customer: window.FREE_GIFT_CUSTOMER ?? null,
      market: window.localization?.market ?? null,
      cart,
    });

    if (action === "noop") {
      lastAttemptedAction = null;
      consecutiveAttempts = 0;
      return;
    }

    consecutiveAttempts = action === lastAttemptedAction ? consecutiveAttempts + 1 : 1;
    lastAttemptedAction = action;

    if (consecutiveAttempts > MAX_CONSECUTIVE_ATTEMPTS) {
      console.error(
        `Free Gift Promo: giving up after ${MAX_CONSECUTIVE_ATTEMPTS} unproductive "${action}" attempts — check FREE_GIFT_VARIANT_ID is a valid variant on this shop and the gift's inventory isn't exhausted.`,
      );
      return;
    }

    if (action === "add") {
      await addToCart({ items: [{ id: String(FREE_GIFT_VARIANT_ID), quantity: 1 }] });
    } else if (action === "remove") {
      const line = findFreeGiftLine(cart);
      if (line?.key) {
        await updateCartItemQuantity({ id: line.key, quantity: 0 });
      }
    }
  } catch (error) {
    console.error("Failed to sync Free Gift Promo line", error);
  } finally {
    isSyncing = false;
    if (needsResync) {
      needsResync = false;
      void syncFreeGiftLine();
    }
  }
}

export function initFreeGiftPromoWatcher() {
  window.addEventListener("cranky:cart:update", () => void syncFreeGiftLine());
  document.addEventListener("alpine:init", () => void syncFreeGiftLine());
}
